const MODES = ['orbit', 'sheet', 'dial'];

function pickMode(el) {
  let mode = el.dataset.mode;
  if (el.dataset.urlSwitch === 'true') {
    const fromUrl = new URLSearchParams(location.search).get('dock');
    try {
      if (MODES.includes(fromUrl)) sessionStorage.setItem('dock-mode', fromUrl);
      const saved = sessionStorage.getItem('dock-mode');
      if (MODES.includes(saved)) mode = saved;
    } catch {
      if (MODES.includes(fromUrl)) mode = fromUrl;
    }
  }
  if (mode === 'auto' || !MODES.includes(mode)) {
    mode = { studio: 'sheet', drop: 'dial', system: 'orbit' }[document.documentElement.dataset.style] || 'orbit';
  }
  return mode;
}

class CapDock extends HTMLElement {
  connectedCallback() {
    this.mode = pickMode(this);
    this.dataset.mode = this.mode;
    this.button = this.querySelector('.dock__cap');
    this.sheet = this.querySelector('.dock__sheet');
    this.items = [...this.querySelectorAll('.dock__item')];
    const bag = document.getElementById('shopify-section-cart-sheet') || document.querySelector('[data-cart-sheet]');
    if (bag) this.querySelector('.sheet__inner').append(bag);
    new ResizeObserver(() => {
      if (this.open && this.mode === 'sheet') this.style.setProperty('--sheet-h', `${this.sheet.offsetHeight}px`);
    }).observe(this.sheet);
    this.querySelector('.dock__items').style.setProperty('--n', this.items.length);
    this.view = null;
    this.index = 0;

    this.intro();
    this.scheduleCap();
    this.bindPointer();
    this.querySelector('[data-close]').addEventListener('click', () => this.setOpen(false));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.open) this.setOpen(false); });
    document.addEventListener('cart:updated', (e) => this.onCart(e.detail.count, e.detail.source));


    if (this.mode === 'dial') {
      this.button.setAttribute('aria-label', this.items.map((a) => a.textContent.trim()).join(', '));
      this.renderDial(false);
      this.showHint();
      this.dialing(4000);
      this.button.addEventListener('pointerdown', () => this.dialing());
    }
  }

  scheduleCap() {
    const start = () => {
      if (this.capStarted) return;
      this.capStarted = true;
      this.loadCap();
    };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 500 }) : setTimeout(start, 200));
    if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
    this.button.addEventListener('pointerenter', start, { once: true });
    this.button.addEventListener('touchstart', start, { once: true, passive: true });
  }

  intro() {
    let seen = false;
    try { seen = !!localStorage.getItem('dc-intro'); } catch {}
    if (seen || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.classList.add('is-intro');
    const tip = document.createElement('span');
    tip.className = 'dock__tip';
    tip.textContent = this.dataset.tip || 'Tap the cap!';
    this.append(tip);
    setTimeout(() => this.view?.squish(0.3), 1050);
    const done = () => {
      this.classList.remove('is-intro');
      tip.remove();
      try { localStorage.setItem('dc-intro', '1'); } catch {}
    };
    setTimeout(done, 5200);
    this.button.addEventListener('pointerdown', done, { once: true });
  }

  async loadCap() {
    try {
      const { createCapView } = await import(new URL(this.dataset.module, document.baseURI).href);
      this.view = createCapView(this.querySelector('.dock__canvas'), {
        sprite: new URL(this.dataset.sprite, document.baseURI).href,
        onReady: () => this.classList.add('is-ready'),
      });
    } catch (err) {
      this.button.classList.add('no-3d');
      console.warn('Cap dock: 3D unavailable', err);
    }
  }

  bindPointer() {
    let startX = 0, lastX = 0, startT = 0, dragging = false, moved = false;
    this.button.addEventListener('pointerdown', (e) => {
      dragging = true; moved = false; startX = lastX = e.clientX; startT = performance.now();
      this.button.setPointerCapture(e.pointerId);
      this.view?.squish(0.12);
    });
    this.button.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      if (Math.abs(e.clientX - startX) > 6) moved = true;
      this.view?.nudge(dx * 0.012);
    });
    this.button.addEventListener('pointerup', (e) => {
      if (!dragging) return;
      dragging = false;
      const total = e.clientX - startX;
      const fast = Math.abs(total) / Math.max(1, performance.now() - startT) > 0.8;
      if (this.mode === 'dial' && Math.abs(total) > 24) this.step((total < 0 ? 1 : -1) * (fast ? 2 : 1));
      else if (!moved) this.tap();
      else this.view?.turnTo(this.restAngle());
    });
    this.button.addEventListener('pointercancel', () => { dragging = false; });
    this.button.addEventListener('keydown', (e) => {
      if (this.mode !== 'dial') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
    });
    this.button.addEventListener('click', (e) => { if (e.detail === 0) this.tap(); });
    this.querySelectorAll('.dial__btn').forEach((b) => b.addEventListener('click', () => this.step(+b.dataset.step)));
  }

  restAngle() {
    return this.mode === 'dial' ? -0.55 - this.index * ((Math.PI * 2) / this.items.length) : -0.55;
  }

  tap() {
    this.view?.squish();
    if (this.mode === 'dial') {
      const link = this.items[this.index];
      if (link) location.href = link.href;
      return;
    }
    this.setOpen(!this.open);
  }

  setOpen(open) {
    this.open = open;
    if (this.classList.contains('is-intro')) {
      this.classList.remove('is-intro');
      this.querySelector('.dock__tip')?.remove();
      try { localStorage.setItem('dc-intro', '1'); } catch {}
    }
    document.documentElement.classList.toggle('dock-open', open);
    this.classList.toggle('is-open', open);
    this.button.setAttribute('aria-expanded', open);
    this.view?.setFlipped(open);
    this.view?.turnTo(open ? 0.2 : -0.55);
    if (this.mode === 'sheet') {
      if (open) {
        this.sheet.show();
        this.style.setProperty('--sheet-h', `${this.sheet.offsetHeight}px`);
        this.sheet.querySelector('.sheet__links a')?.focus({ preventScroll: true });
      } else {
        setTimeout(() => { if (!this.open) this.sheet.close(); }, 450);
        this.button.focus({ preventScroll: true });
      }
    }
    if (this.mode === 'orbit' && open) this.items[0]?.focus({ preventScroll: true });
  }

  step(dir) {
    const n = this.items.length;
    this.index = (this.index + dir + n) % n;
    this.view?.turnTo(this.restAngle());
    this.view?.squish(0.1);
    navigator.vibrate?.(8);
    this.dialing();
    this.renderDial(true);
    this.hideHint();
  }

  dialing(ms = 2500) {
    this.classList.add('is-dialing');
    clearTimeout(this.dialTimer);
    this.dialTimer = setTimeout(() => this.classList.remove('is-dialing'), ms);
  }

  renderDial(animate) {
    const n = this.items.length;
    const label = (i) => this.items[(i + n) % n]?.textContent.trim().replace(/\s+/g, ' ') ?? '';
    this.querySelector('.dial__prev').textContent = label(this.index - 1);
    this.querySelector('.dial__current').textContent = label(this.index);
    this.querySelector('.dial__next').textContent = label(this.index + 1);
    if (animate) {
      this.classList.remove('is-ticked');
      void this.offsetWidth;
      this.classList.add('is-ticked');
    }
  }

  showHint() {
    try { if (localStorage.getItem('dock-hint-seen')) return; } catch {}
    this.hint = document.createElement('span');
    this.hint.className = 'dock__hint';
    this.hint.textContent = this.dataset.hint || 'Swipe the cap';
    this.append(this.hint);
  }

  hideHint() {
    if (!this.hint) return;
    this.hint.style.opacity = 0;
    try { localStorage.setItem('dock-hint-seen', '1'); } catch {}
  }

  onCart(count, source) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      if (el.classList.contains('dock__count')) el.hidden = count === 0;
    });
    this.classList.toggle('has-items', count > 0);
    if (source === 'panel') { this.view?.squish(0.12); return; }
    this.view?.hop();
    this.view?.spin(1);
    this.classList.remove('is-bumped');
    void this.offsetWidth;
    this.classList.add('is-bumped');
  }
}

customElements.define('cap-dock', CapDock);
